
import { useState, useMemo, useCallback } from 'react';
import ProductCard from './ProductCard';

interface ProductGridProps {
  products: any[];
  onAddToCart: (product: any, quantity: number) => void;
  selectedCategory?: string;
  onCategoryChange?: (category: string) => void;
  searchQuery?: string;
}

export default function ProductGrid({ products, onAddToCart, selectedCategory = 'alle', onCategoryChange, searchQuery = '' }: ProductGridProps) {
  const [sortBy, setSortBy] = useState('name');
  const [showAllCategories, setShowAllCategories] = useState(false);

  // KORRIGIERTE KATEGORIE-ZUORDNUNG - Frontend ID → Datenbank Kategorie
  const categoryMapping = {
    'alle': 'alle',
    'suesswaren-schokolade': 'suesswaren-schokolade',
    'getraenke': 'getraenke', // ✅ KORRIGIERT: Separate alkoholfreie Getränke
    'alkohol': 'getraenke-alkohol', // ✅ KORRIGIERT: Exakte Datenbank-Kategorie!
    'snacks': 'snacks', // ✅ NEU: Snacks Kategorie hinzugefügt
    'zubehoer-artikel': 'zubehoer-artikel',
    'convenience-produkte': 'convenience-produkte',
    'haushalt-pflege': 'haushalt-pflege',
    'nikotinerzeugnisse': 'nikotinerzeugnisse',
  };

  const categories = [
    { id: 'alle', name: 'Alle Kategorien', icon: 'ri-apps-line' },
    { id: 'suesswaren-schokolade', name: 'Snacks, Süsswaren & Schokolade', icon: 'ri-cake-line' },
    { id: 'getraenke', name: 'Getränke', icon: 'ri-cup-line' }, // ✅ Alkoholfreie Getränke
    { id: 'alkohol', name: 'Getränke/Alkohol', icon: 'ri-goblet-line' }, // ✅ Zeigt getraenke-alkohol Kategorie
    { id: 'snacks', name: 'Snacks', icon: 'ri-apple-line' }, // ✅ GEÄNDERT: Gesunde Snacks → Snacks
    { id: 'zubehoer-artikel', name: 'Zubehör & Artikel', icon: 'ri-tools-line' },
    { id: 'convenience-produkte', name: 'Convenience Produkte', icon: 'ri-shopping-bag-line' },
    { id: 'haushalt-pflege', name: 'Haushalt & Pflege', icon: 'ri-home-line' },
    { id: 'nikotinerzeugnisse', name: 'Nikotinerzeugnisse', icon: 'ri-fire-line' },
  ];

  // VERBESSERTE Filterung - Mit korrekter Kategorie-Zuordnung
  const filteredProducts = useMemo(() => {
    let filtered = products;
    
    // Lokale Suche wenn Server-Suche nicht funktioniert
    if (searchQuery && searchQuery.length >= 2) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(product => 
        product.name.toLowerCase().includes(query) ||
        product.category?.toLowerCase().includes(query) ||
        product.description?.toLowerCase().includes(query) ||
        product.sku?.toLowerCase().includes(query)
      );
    }
    
    // KORRIGIERTE Kategorie-Filterung - EXAKTE DATENBANK-KATEGORIEN
    if (selectedCategory !== 'alle' && !onCategoryChange) {
      const dbCategory = categoryMapping[selectedCategory] || selectedCategory;
      
      // SPEZIAL: Getränke/Alkohol zeigt die exakte Datenbank-Kategorie "getraenke-alkohol"
      if (selectedCategory === 'alkohol') {
        filtered = filtered.filter(product => {
          const productCategory = product.category?.toLowerCase() || '';
          
          // ✅ EXAKTE SUCHE nach der Datenbank-Kategorie "getraenke-alkohol"
          return productCategory === 'getraenke-alkohol';
        });
      } else if (selectedCategory === 'getraenke') {
        // Nur alkoholfreie Getränke - exakte Kategorie "getraenke"
        filtered = filtered.filter(product => {
          const productCategory = product.category?.toLowerCase() || '';
          return productCategory === 'getraenke';
        });
      } else {
        filtered = filtered.filter(product => {
          const productCategory = product.category?.toLowerCase() || '';
          return productCategory === dbCategory.toLowerCase();
        });
      }
    }
    
    return filtered;
  }, [products, selectedCategory, searchQuery, onCategoryChange]);

  // Hochoptimierte Sortierung
  const sortedProducts = useMemo(() => {
    if (filteredProducts.length === 0) return [];
    
    const sorted = [...filteredProducts];
    
    switch (sortBy) {
      case 'price-low':
        return sorted.sort((a, b) => a.price - b.price);
      case 'price-high':
        return sorted.sort((a, b) => b.price - a.price);
      case 'name':
        return sorted.sort((a, b) => a.name.localeCompare(b.name, 'de'));
      case 'stock':
        return sorted.sort((a, b) => b.stock - a.stock);
      default:
        return sorted;
    }
  }, [filteredProducts, sortBy]);

  // Optimierte Statistiken
  const stats = useMemo(() => {
    if (sortedProducts.length === 0) return { available: 0, low: 0, outOfStock: 0 };
    
    let available = 0, low = 0, outOfStock = 0;
    
    for (const product of sortedProducts) {
      if (product.stock === 0) outOfStock++;
      else if (product.stock <= 10) low++;
      else available++;
    }
    
    return { available, low, outOfStock };
  }, [sortedProducts]);

  // Verfügbare Kategorien - ALLE KATEGORIEN IMMER ANZEIGEN
  const availableCategories = useMemo(() => {
    return categories;
  }, [categories]);

  // Optimierte Event-Handler - Mit korrekter Kategorie-Zuordnung
  const handleCategoryChange = useCallback((categoryId: string) => {
    if (onCategoryChange) {
      // Sende die korrekte Datenbank-Kategorie an die API
      const dbCategory = categoryMapping[categoryId] || categoryId;
      onCategoryChange(dbCategory);
    }
    setShowAllCategories(false);
  }, [onCategoryChange]);

  const handleSortChange = useCallback((newSortBy: string) => {
    setSortBy(newSortBy);
  }, []);

  return (
    <div>
      {/* Mobile-optimierte Filter */}
      <div className="mb-4 sm:mb-6">
        <div className="flex flex-col space-y-3 sm:space-y-4 lg:flex-row lg:items-center lg:justify-between lg:space-y-0 gap-4">
          
          {/* Mobile Kategorien - Dropdown für kleine Bildschirme */}
          <div className="sm:hidden">
            <button
              onClick={() => setShowAllCategories(!showAllCategories)}
              className={`w-full flex items-center justify-between px-4 py-3 rounded-lg text-sm font-medium transition-all border ${
                selectedCategory === 'alle'
                  ? 'bg-orange-500 text-white border-orange-500'
                  : 'bg-white text-gray-700 border-gray-200'
              }`}
            >
              <div className="flex items-center space-x-2">
                <i className={categories.find(c => c.id === selectedCategory)?.icon || 'ri-apps-line'}></i>
                <span>{categories.find(c => c.id === selectedCategory)?.name || 'Alle'}</span>
              </div>
              <i className={`ri-arrow-${showAllCategories ? 'up' : 'down'}-s-line`}></i>
            </button>
            
            {showAllCategories && (
              <div className="mt-2 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
                {availableCategories.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => handleCategoryChange(category.id)}
                    className={`w-full flex items-center space-x-3 px-4 py-3 text-left text-sm transition-colors cursor-pointer ${
                      selectedCategory === category.id
                        ? 'bg-orange-50 text-orange-600 border-l-4 border-orange-500'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <i className={category.icon}></i>
                    <span>{category.name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Desktop Kategorien - Horizontale Buttons */}
          <div className="hidden sm:flex flex-wrap gap-2">
            {availableCategories.map((category) => (
              <button
                key={category.id}
                onClick={() => handleCategoryChange(category.id)}
                className={`flex items-center space-x-2 px-3 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                  selectedCategory === category.id
                    ? 'bg-orange-500 text-white shadow-md'
                    : 'bg-white text-gray-700 hover:bg-orange-50 border border-gray-200'
                }`}
              >
                <i className={category.icon}></i>
                <span className="hidden md:inline">{category.name}</span>
                <span className="md:hidden">{category.name.split(' ')[0]}</span>
              </button>
            ))}
          </div>

          {/* Mobile-optimierte Sortierung */}
          <div className="flex items-center justify-between sm:justify-end space-x-3">
            <span className="text-sm text-gray-600 sm:inline">Sortieren:</span>
            <select
              value={sortBy}
              onChange={(e) => handleSortChange(e.target.value)}
              className="flex-1 sm:flex-none px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white pr-8"
            >
              <option value="name">Name</option>
              <option value="price-low">Preis ↑</option>
              <option value="price-high">Preis ↓</option>
              <option value="stock">Lagerbestand</option>
            </select>
          </div>
        </div>
      </div>

      {/* Mobile-optimierte Übersicht */}
      <div className="mb-4 sm:mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex flex-col sm:flex-row sm:items-center gap-2">
            <p className="text-gray-600 text-sm">
              <span className="font-semibold">{sortedProducts.length}</span> Produkte 
              {selectedCategory !== 'alle' && ` in ${categories.find(c => c.id === selectedCategory)?.name}`}
              {searchQuery && ` für "${searchQuery}"`}
            </p>
            
            {searchQuery && (
              <div className="flex items-center space-x-2 text-xs bg-orange-50 text-orange-600 px-2 py-1 rounded-full">
                <i className="ri-search-line"></i>
                <span>Suche aktiv</span>
              </div>
            )}
          </div>
          
          {/* Mobile-optimierte Status-Punkte */}
          <div className="flex items-center space-x-3 sm:space-x-4 text-xs">
            <div className="flex items-center space-x-1">
              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              <span className="text-gray-600">{stats.available}</span>
              <span className="hidden sm:inline text-gray-600">verfügbar</span>
            </div>
            <div className="flex items-center space-x-1">
              <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
              <span className="text-gray-600">{stats.low}</span>
              <span className="hidden sm:inline text-gray-600">niedrig</span>
            </div>
            <div className="flex items-center space-x-1">
              <div className="w-2 h-2 bg-red-500 rounded-full"></div>
              <span className="text-gray-600">{stats.outOfStock}</span>
              <span className="hidden sm:inline text-gray-600">ausverkauft</span>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile-optimiertes Produktraster */}
      {sortedProducts.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4" data-product-shop>
          {sortedProducts.map((product) => (
            <ProductCard
              key={product.id}
              product={product}
              onAddToCart={onAddToCart}
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-8 sm:py-12">
          <i className="ri-search-line text-3xl sm:text-4xl text-gray-300 mb-3"></i>
          <h3 className="text-base sm:text-lg font-semibold text-gray-600 mb-2">
            {searchQuery ? 'Keine Suchergebnisse' : 'Keine Produkte gefunden'}
          </h3>
          <p className="text-gray-500 text-sm px-4">
            {searchQuery 
              ? `Keine Produkte für "${searchQuery}" gefunden. Versuchen Sie andere Suchbegriffe.`
              : selectedCategory !== 'alle' 
                ? `Keine Produkte in der Kategorie "${categories.find(c => c.id === selectedCategory)?.name}" gefunden`
                : 'Versuchen Sie eine andere Kategorie'
            }
          </p>
          {searchQuery && (
            <button
              onClick={() => window.location.reload()}
              className="mt-4 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors cursor-pointer"
            >
              Alle Produkte anzeigen
            </button>
          )}
        </div>
      )}
    </div>
  );
}
